import React from 'react'
import {Button, SplitColumnsContainer} from 'startlibs/lib/components'
import {Field, Errors, TextInput} from 'startlibs/lib/form'
import {withToggles} from 'startlibs/lib/hocs'
import load from 'little-loader'
import {STRIPE_KEY} from '../config/StripeConfig'
import {CreditCardIcon, CreditCardWrapper, StripeInput} from './CheckoutComponents'
import {ice} from 'icepath'
import {AJAX_ERROR, postFetcher} from 'startlibs'
import {buildValidation, minLength, required} from '../lib/validation'
import {CreditCardDetails} from '../user/Billing'
import {executeCaptcha, RecaptchaBrandingText, waitCaptcha} from "./CaptchaUtils";

@withToggles('loading', 'updateCard')
export class CreditCardCheckout extends React.PureComponent {

  state = {}

  cardErrors = {
    cardCvc: {message: 'Your card\'s security code is incomplete.'},
    cardExpiry: {message: 'Your card\'s expiration date is incomplete.'},
    card: {message: 'Your card number is incomplete.'}
  }

  preValidation = buildValidation({
      cardholder: required,
      country: required,
      businessName: this.props.skipContactInfo ? () => false : required,
      phoneNumber: this.props.skipContactInfo ? () => false : required,
      zipCode: [required, minLength(4)]
    }
  )
  onlyPlanPreValidation = buildValidation({
      businessName: this.props.skipContactInfo ? () => false : required,
      phoneNumber: this.props.skipContactInfo ? () => false : required
    }
  )
  captchaRef = {current:null}

  componentDidMount() {
    if (this.props.isCurrentPlanSelected) {
      return this.props.prevStep()
    }

    Promise.all([this.loadStripe(),waitCaptcha((token) => {
      this.handleCaptchaSuccess(token)
    },this.captchaRef)]).then(([Stripe,captcha]) =>
      this.setState({stripe: Stripe(STRIPE_KEY)})
    )
    this.props.form.connector.clearErrors()
    window.scroll(0, 0)
    if (!this.props.currentCard) {
      this.props.updateCard.open()
    }
  }

  componentWillUnmount() {
    this.props.form.connector.clearErrors()
  }

  componentDidUpdate(oldProps, oldState) {
    if (oldState.stripe !== this.state.stripe) {
      const elements = this.state.stripe.elements()
      const style = {
        base: {
          color: '#3c3c3c',
          lineHeight: '18px',
          fontFamily: '"Open Sans", sans-serif',
          fontSize: '14px',
          '::placeholder': {
            color: 'rgba(0,0,0,0.35)'
          }
        },
        invalid: {
          color: '#C3282D',
          iconColor: '#fa755a'
        }
      }

      const cardExpiry = elements.create('cardExpiry', {style})
      cardExpiry.mount('#card-expiry-element')
      cardExpiry.on('change', ({error}) => {
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, cardExpiry: error}
      })

      const cardCvc = elements.create('cardCvc', {style})
      cardCvc.mount('#card-cvc-element')
      cardCvc.on('change', ({error}) => {
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, cardCvc: error}
      })

      this.card = elements.create('cardNumber', {style})
      this.card.on('change', (event) => {
        this.setState({cardBrand: event.brand})
        this.cardModified = true
        this.cardErrors = {...this.cardErrors, card: event.error}
      })
      this.card.mount('#card-element')
    }
  }

  loadStripe = () =>
    window.Stripe ? Promise.resolve(window.Stripe) : new Promise((res, rej) =>
      load('https://js.stripe.com/v3/', (err) => {
        if (err) { rej() }
        res(window.Stripe)
      })
    )


  handleSubmit = (e) => {
    e.preventDefault()
    this.props.loading.open()
    if (this.props.updateCard.isOpen) {
      this.validate().then(() => executeCaptcha()).catch(() => {this.props.loading.close()})
    } else {
      executeCaptcha().catch(() => {this.props.loading.close()})
    }
  }

  handleCaptchaSuccess = (captchaToken) => {
    if (this.props.updateCard.isOpen) {
      this.submitSubscriptionUpdate(captchaToken)
    } else {
      this.submitPlanUpdate(captchaToken)
    }
  }

  submitPlanUpdate = (captchaToken) => {
    const {loading, skipContactInfo, nextStep, form, workstationsVPN, prorate, coupon} = this.props
    const stripePlanId = this.props.finalPlan.id
    const values = this.props.form.properties
    this.props.form.connector.clearErrors()
    const validation = this.onlyPlanPreValidation(form.properties, {})
    if (validation.errors) {
      form.connector.setErrors(validation.errors)
      return
    }
    loading.open()
    Promise.all([
        skipContactInfo
        ? Promise.resolve()
        : postFetcher('/api/contact-info', {businessName: values.businessName, phoneNumber: values.phoneNumber}),
        postFetcher('/api/update-subscription', {
          stripeCardToken: ice.get(this.props.currentCard, 'id'),
          stripePlanId,
          captchaToken,
          subscriptionType: 'STORAGE',
          workstationsVPN,
          ...(coupon && prorate && prorate.discount ? {coupon} : {})
        }, {method: 'PUT'})
      ])
      .then(nextStep)
      .catch(loading.close)
  }
  stripeResult = null

  validate = () => {
    const {loading, form} = this.props
    const {stripe} = this.state
    const validated = this.preValidation(form.properties, {})
    const cardErrors = ice(this.cardErrors).filter((v) => v).map((v) => [v.message]).value()
    const stripeValidation = Object.keys(cardErrors).length ?
      stripe.createToken(this.card).then((result) => ({card: [result.error.message]})) :
      Promise.resolve({})

    if (validated.errors) {
      stripeValidation.then((stripeErrors) => form.connector.setErrors({...validated.errors, ...cardErrors}))
      return
    }
    loading.open()
    const values = form.properties
    return Promise.all([
      stripe.createToken(this.card, {
        address_zip: values.zipCode,
        address_country: values.country,
        name: values.cardholder
      }),
      postFetcher('/api/contact-info', {businessName: values.businessName, phoneNumber: values.phoneNumber})
    ]).then(([stripeResult, profileResult]) => {
      if (stripeResult.error || profileResult.error) {
        return Promise.reject(stripeResult.error ? stripeResult : profileResult)
      } else {
        this.stripeResult = stripeResult
      }
    }).catch((result) => {
      if (result && result.error) {
        form.connector.setErrors({card: [result.error.message]})
      } else {
        form.connector.setErrors({form: [AJAX_ERROR]})
      }
      loading.close()
      return Promise.reject()
    })
  }

  submitSubscriptionUpdate = (captchaToken) => {
    const {loading, nextStep} = this.props
    // Send the token to your server.
    const stripePlanId = this.props.finalPlan.id
    const coupon = this.props.appliedCoupon
    const workstationsVPN = this.props.workstationsVPN

    postFetcher('/api/update-subscription', {
      captchaToken,
      stripeCardToken: this.stripeResult.token.id,
      stripePlanId,
      subscriptionType: 'STORAGE',
      workstationsVPN,
      ...(coupon ? {coupon} : {})
    }, {method: 'PUT'})
      .then(nextStep)
      .catch((e) => {
        this.props.loading.close()
        this.props.form.connector.setErrors({"card":[e && e.errorResponse ? e.errorResponse.detail || e.errorResponse.title : AJAX_ERROR]})
      })
  }

  render() {
    const {form, skipContactInfo, currentCard, hasCurrentActivePlan, isStorage, updateCard, loading, prevStep, orderPreview} = this.props
    const {cardBrand} = this.state
    return <div className="content edit-profile">
      <div className={'profile-header checkout-header'}>
        <h1>{hasCurrentActivePlan && "Update "}Horos {isStorage ? "Access" : "Cloud"} subscription</h1>
        <Button.a small href='https://info.horosproject.org/horos-cloud-faq' target="_blank">Learn more</Button.a>
      </div>
      {orderPreview(<Button small onClick={prevStep}>Change order</Button>)}
      <form>
        {
          !updateCard.isOpen && currentCard &&
          <React.Fragment>
            <h3>Credit card details</h3>
            <p>
              Subscription charges for the selected plans will be done in the following credit card registered in your
              account. You can change it clicking on the button bellow (all your subscriptions will be charged to the
              new credit card).
            </p>
            <CreditCardDetails card={currentCard}>
              <Button small onClick={updateCard.open}>Change credit card</Button>
            </CreditCardDetails>
          </React.Fragment>
        }
        <CreditCardWrapper>
          <div className={updateCard.isOpen ? 'expanded-card' : 'hidden'}>
            <div className="update-card-heading">
              <div className="heading-text">
                <h3>Credit card details</h3>
                {
                  !currentCard ?
                  <p>Please fill in your new credit card details below:</p> :
                  <p>By changing your credit card, <b>all subscriptions will be charged according to the new registered card</b>.</p>
                }
              </div>
              <div className="action-wrapper">
                {currentCard && <Button onClick={() => {
                  updateCard.close()
                  form.connector.clearErrors()
                }}>Use current credit card instead</Button>}
              </div>
            </div>
            <div className="card billing-card">
              <TextInput
                form={form}
                autoFocus
                type="text"
                path="cardholder"
                label="Name on card:"
                placeholder="Ex: John Doe"
              />
              <Field label="Card number:">
                <StripeInput id="card-element"/>
                <CreditCardIcon src={`/public/icon/${cardBrand || 'unknown'}.svg`}/>
              </Field>
              <SplitColumnsContainer>
                <Field label="Expiration:">
                  <StripeInput id="card-expiry-element"/>
                </Field>
                <Field label="CVC:">
                  <StripeInput id="card-cvc-element"/>
                </Field>
              </SplitColumnsContainer>
              <SplitColumnsContainer>
                <TextInput
                  form={form}
                  type="text"
                  path="country"
                  label="Country:"
                  placeholder="Ex: USA"
                />
                <TextInput
                  form={form}
                  type="text"
                  path="zipCode"
                  label="Zip code:"
                  placeholder="Ex: 21401"
                />
              </SplitColumnsContainer>
            </div>
          </div>
        </CreditCardWrapper>
        {
          !skipContactInfo &&
          <CreditCardWrapper>
            <div className='expanded-card'>
              <div className="update-card-heading">
                <div className="heading-text">
                  <h3>Contact details</h3>
                  <p>We will contact you in 1 business day to start you cloud storage setup.</p>
                </div>
              </div>
              <div className="card billing-card">
                <TextInput
                  form={form}
                  path="businessName"
                  label="Business Name:"
                />
                <TextInput
                  form={form}
                  setValue={(newValue,_,setValue) => setValue(newValue.replace(/[^0-9()\-+\s]/,''))}
                  path="phoneNumber"
                  label="Phone Number:"
                />
              </div>
            </div>
          </CreditCardWrapper>
        }
        <Errors form={form}/>
      </form>
      <div className="profile-footer">
        <Button onClick={prevStep}>Return</Button>
        <Button highlight isLoading={loading.isOpen} onClick={this.handleSubmit}>Subscribe</Button>
      </div>
      <div
        ref={(el) => this.captchaRef.current = el}
        className="g-recaptcha"
      />
      <div>
        <RecaptchaBrandingText>
          This site is protected by reCAPTCHA and the Google <a
          href="https://policies.google.com/privacy"
          rel="noopener noreferrer"
          target="_blank"
        >
          Privacy Policy</a> and <a
          href="https://policies.google.com/terms"
          rel="noopener noreferrer"
          target="_blank"
        >Terms of Service</a> apply.
        </RecaptchaBrandingText>
      </div>
    </div>
  }

}



// WEBPACK FOOTER //
// ./src/javascripts/checkout/CreditCardCheckout.jsx